/**
 * The Team's people in the queue header: up to three faces and "+N", a real
 * button. Hovering it opens "On this Team" (who is on it, what each answers,
 * who owns it); a click or Enter opens it too and keeps it open, so keyboard
 * and touch readers get the same list. Escape and a click outside close it.
 */
import { useEffect, useRef, useState } from "react";
import type { Job } from "@yagni/shared";

import { Btn } from "../../queue/index.js";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover.js";
import type { TeamAnswerers, TeamMember } from "../teamsApi.js";
import { Face, FaceStack } from "./Face.js";
import { PEOPLE_RULE, STACK_MAX, answersLine, ownerFirst, personName } from "./people.js";
import "./people.css";

/** How long the list waits after the pointer leaves, so it can travel from the faces to the list. */
const CLOSE_DELAY_MS = 160;

export interface PeopleStackProps {
  members: readonly TeamMember[];
  jobs: readonly Job[];
  answerers: TeamAnswerers;
  /** The reader may add and remove people (a workspace admin or the Team's owner). */
  canManage: boolean;
  onAddPeople: () => void;
  onManage: () => void;
}

export function PeopleStack({
  members,
  jobs,
  answerers,
  canManage,
  onAddPeople,
  onManage,
}: PeopleStackProps) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);
  // Opened by the pointer resting on it, not by a press: a press then keeps it.
  const byHover = useRef(false);
  const pinned = useRef(false);

  const cancelClose = () => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  useEffect(() => cancelClose, []);

  const change = (next: boolean) => {
    cancelClose();
    if (!next) {
      pinned.current = false;
      byHover.current = false;
    }
    setOpen(next);
  };
  const hoverOpen = () => {
    cancelClose();
    if (open) return;
    byHover.current = true;
    setOpen(true);
  };
  const hoverClose = () => {
    if (pinned.current) return;
    cancelClose();
    closeTimer.current = window.setTimeout(() => change(false), CLOSE_DELAY_MS);
  };

  const people = ownerFirst(members);
  const count = people.length;
  const label = `${count} ${count === 1 ? "person" : "people"} on this Team`;
  const faces = people.map((member) => ({
    userId: member.userId,
    name: personName(member),
    avatarUrl: member.avatarUrl,
  }));

  return (
    <Popover open={open} onOpenChange={change}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="pstack"
          aria-label={label}
          data-testid="team-people"
          onMouseEnter={hoverOpen}
          onMouseLeave={hoverClose}
          onClick={(event) => {
            // The list is already open under the pointer: a press keeps it open
            // instead of toggling it shut.
            if (open && byHover.current) {
              event.preventDefault();
              byHover.current = false;
              pinned.current = true;
              return;
            }
            pinned.current = !open;
          }}
        >
          <FaceStack people={faces} max={STACK_MAX} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="peoplepop"
        aria-label="On this Team"
        data-testid="team-people-list"
        onMouseEnter={cancelClose}
        onMouseLeave={hoverClose}
        // Hovering must not pull focus out of wherever the reader is typing.
        onOpenAutoFocus={(event) => {
          if (byHover.current) event.preventDefault();
        }}
      >
        <div className="phd">
          <span className="t">On this Team</span>
          <span className="fact">{count}</span>
        </div>
        <div className="plist">
          {people.map((member) => (
            <div className="prow" key={member.userId} data-testid="team-person">
              <Face
                userId={member.userId}
                name={personName(member)}
                avatarUrl={member.avatarUrl}
                size={28}
              />
              <span className="who">
                <span className="nm">{personName(member)}</span>
                <span className="sub">{answersLine(member.userId, jobs, answerers)}</span>
              </span>
              <span className={`chip role${member.role === "owner" ? " owner" : ""}`}>
                {member.role === "owner" ? "Owner" : "Member"}
              </span>
            </div>
          ))}
        </div>
        <div className="pfoot">
          <p>{PEOPLE_RULE}</p>
          <div className="verbs">
            <Btn
              variant="quiet"
              size="sm"
              onClick={() => {
                change(false);
                onManage();
              }}
            >
              Manage
            </Btn>
            {canManage && (
              <Btn
                variant="secondary"
                size="sm"
                data-testid="team-people-add"
                onClick={() => {
                  change(false);
                  onAddPeople();
                }}
              >
                Add people
              </Btn>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
